import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, Download, ClipboardList, LayoutTemplate } from 'lucide-react'
import Popup from 'components/Popup'
import SiteDetailTemplatesSelectCN from 'components/SiteDetailTemplatesSelectCN'
import BuildSiteDetailTemplateModal from 'components/popups/BuildSiteDetailTemplateModal'
import { Button } from 'components/ui/button'
import Spinner from 'components/Spinner'
import Details from 'features/Details'
import { useDetails } from 'features/Details/DetailsContext'
import { patchLocation } from 'slices/locationSlice'
import notify from 'components/ui/notify'

import {
	createSiteDetail,
	getSiteDetails,
	updateSiteDetail,
	loadTemplateIntoSiteDetails,
	checkDeletedDuplicateSiteDetail,
	checkDeletedDuplicateSiteDetails,
	restoreSiteDetail,
} from 'slices/siteDetailsSlice'
import { initializeTemplates, buildTemplateFromSiteDetails } from 'slices/templateSlice'
import { patchProject } from 'slices/projectSlice'
import LocationDetailsEmptyState from 'components/LocationDetailsEmptyState'
import DeletedDuplicateDialog from 'components/popups/DeletedDuplicateDialog'

const LocationDetailsScreen = ({ location, project, user }) => {
	const dispatch = useDispatch()

	const locationId = location._id
	const projectId = project._id
	const userId = user._id

	const {
		state: { openPopup, popupTitle, recordForEdit },
		actions: { setOpenPopup, setPopupTitle, setRecordForEdit },
	} = useDetails()

	const [openTemplatePopup, setOpenTemplatePopup] = useState(false)
	const [openBuildTemplatePopup, setOpenBuildTemplatePopup] = useState(false)
	const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
	const [duplicateMatches, setDuplicateMatches] = useState([])
	const [pendingDetailPayload, setPendingDetailPayload] = useState(null)
	const [pendingResetForm, setPendingResetForm] = useState(null)
	const [pendingTemplateId, setPendingTemplateId] = useState(null)
	const [isDuplicateLoading, setIsDuplicateLoading] = useState(false)

	const siteDetailsSelector = useSelector(state => state.siteDetails)
	const { siteDetails, loading: isSiteDetailsLoading, isInitialized } = siteDetailsSelector

	const {
		entities: templateEntities,
		ids: templateIds,
		isInitialized: templatesInitialized,
	} = useSelector(state => state.templates)
	const newTemplates = templateIds.map(id => templateEntities[id])
	const siteDetailTemplates = newTemplates.filter(item => item.templateType === 'sitedetail')
	const hasDetails = siteDetails && siteDetails.length > 0

	useEffect(() => {
		if (!isInitialized) {
			dispatch(getSiteDetails({ projectId }))
		}
		dispatch(initializeTemplates())
	}, [dispatch, isInitialized, userId])

	const buildDetailPayload = (siteDetail) => {
		const { detailLabel, description, detailType, detail } = siteDetail
		return {
			packageid: locationId,
			projectid: projectId,
			packageType: 'location',
			detailLabel,
			description,
			detail,
			detailType,
			detailTextValue: detailType === 'Text' ? detail : null,
			detailNumericValue: detailType === 'Number' ? detail : null,
			detailCurrencyValue: detailType === 'Currency' ? detail : null,
			detailDateValue: detailType === 'Date' ? detail : null,
		}
	}

	const finishDetailCreate = () => {
		dispatch(patchLocation({ locationId, updates: { updateNote: 'New Detail(s) Added' } }))
		dispatch(patchProject({ projectId, updates: { updateNote: 'New Detail(s) Added' } }))
		dispatch(getSiteDetails({ projectId }))
	}

	const addDetail = async (siteDetail, resetForm) => {
		const payload = buildDetailPayload(siteDetail)

		// Check for soft-deleted duplicate
		const result = await dispatch(
			checkDeletedDuplicateSiteDetail({ detailLabel: siteDetail.detailLabel, projectid: projectId }),
		)

		if (result.payload?.hasDuplicate) {
			const deleted = result.payload.deletedItem
			setDuplicateMatches([{ name: deleted.detailLabel, id: deleted._id }])
			setPendingDetailPayload(payload)
			setPendingResetForm(() => resetForm)
			setDuplicateDialogOpen(true)
			return
		}

		await dispatch(createSiteDetail(payload))
		finishDetailCreate()
		resetForm()
		setOpenPopup(false)
		setRecordForEdit(null)
	}

	const handleDetailDuplicateRestore = async () => {
		if (duplicateMatches.length === 0) return
		setIsDuplicateLoading(true)
		try {
			for (const match of duplicateMatches) {
				await dispatch(restoreSiteDetail(match.id))
			}
			// If this was a single-create scenario, also load template remaining items
			if (pendingTemplateId) {
				const template = {
					userId,
					packageid: locationId,
					projectid: projectId,
					packageType: 'location',
					templateId: pendingTemplateId,
				}
				await dispatch(loadTemplateIntoSiteDetails(template))
			}
			finishDetailCreate()
			setDuplicateDialogOpen(false)
			setDuplicateMatches([])
			if (pendingResetForm) pendingResetForm()
			setPendingDetailPayload(null)
			setPendingResetForm(null)
			setPendingTemplateId(null)
			setOpenPopup(false)
			setOpenTemplatePopup(false)
			setRecordForEdit(null)
		} finally {
			setIsDuplicateLoading(false)
		}
	}

	const handleDetailDuplicateCreateNew = async () => {
		setIsDuplicateLoading(true)
		try {
			if (pendingDetailPayload && !pendingTemplateId) {
				// Single-create: create the new detail
				await dispatch(createSiteDetail(pendingDetailPayload))
			}
			if (pendingTemplateId) {
				// Template load: proceed normally — partial index allows creation alongside deleted items
				const template = {
					userId,
					packageid: locationId,
					projectid: projectId,
					packageType: 'location',
					templateId: pendingTemplateId,
				}
				await dispatch(loadTemplateIntoSiteDetails(template))
			}
			finishDetailCreate()
			setDuplicateDialogOpen(false)
			setDuplicateMatches([])
			if (pendingResetForm) pendingResetForm()
			setPendingDetailPayload(null)
			setPendingResetForm(null)
			setPendingTemplateId(null)
			setOpenPopup(false)
			setOpenTemplatePopup(false)
			setRecordForEdit(null)
		} finally {
			setIsDuplicateLoading(false)
		}
	}

	const handleEditDeleteDetail = async (siteDetail, resetForm) => {
		const detailLabel = siteDetail.detailLabel
		const description = siteDetail.description
		const detailType = siteDetail.detailType
		const detail = siteDetail.detail

		await dispatch(
			updateSiteDetail({
				_id: siteDetail._id,
				packageid: locationId,
				projectid: projectId,
				packageType: 'location',
				userid: userId,
				detailLabel,
				description: description,
				detail: detail,
				detailType: detailType,
				detailTextValue: detailType === 'Text' ? detail : null,
				detailNumericValue: detailType === 'Number' ? detail : null,
				detailCurrencyValue: detailType === 'Currency' ? detail : null,
				detailDateValue: detailType === 'Date' ? detail : null,
			}),
		)

		dispatch(
			patchLocation({
				locationId: locationId,
				updates: { updateNote: 'Details(s) Updated' },
			}),
		)
		dispatch(patchProject({ projectId: projectId, updates: { updateNote: 'Detail(s) Updated' } }))

		dispatch(getSiteDetails({ projectId }))

		resetForm()
		setOpenPopup(false)
		setRecordForEdit(null)
	}

	const handleTemplateLoad = async (templateId) => {
		// Check for soft-deleted duplicates before loading
		const checkResult = await dispatch(
			checkDeletedDuplicateSiteDetails({ templateId, projectid: projectId }),
		)

		const matches = checkResult.payload?.matches || []
		if (matches.length > 0) {
			setDuplicateMatches(matches.map(m => ({ name: m.name, id: m.deletedItemId })))
			setPendingTemplateId(templateId)
			setDuplicateDialogOpen(true)
			return
		}

		// No duplicates — proceed normally
		const template = {
			userId,
			packageid: locationId,
			projectid: projectId,
			packageType: 'location',
			templateId,
		}
		await dispatch(loadTemplateIntoSiteDetails(template))
		setOpenTemplatePopup(false)
		dispatch(getSiteDetails({ projectId }))
	}

	const handleAddDetail = () => {
		const newDetail = {
			_id: 0,
			detailLabel: '',
			description: '',
			detailType: '',
			detail: '',
		}
		setPopupTitle('New Detail')
		setRecordForEdit(newDetail)
		setOpenPopup(true)
	}

	const handleAddTemplate = () => {
		setPopupTitle('New Template')
		setRecordForEdit({})
		setOpenTemplatePopup(true)
	}

	return (
		<div>
			{hasDetails && (
				<div className="flex flex-wrap justify-between items-center gap-4 mb-6">
					<div className="flex flex-col gap-1">
						<h2 className="text-xl font-bold text-gray-800 m-0">Location Details</h2>
						<span className="text-sm text-gray-500">
							Capture important site-specific information for this location.
						</span>
					</div>
					<div className="flex items-center gap-3 flex-wrap">
						<Button onClick={handleAddDetail}>
							<Plus className="w-4 h-4 mr-1" />
							<span>Create New Detail</span>
						</Button>
						<Button variant="ghost" onClick={handleAddTemplate}>
							<Download className="w-4 h-4 mr-1" />
							<span>Download Template</span>
						</Button>
						<Button variant="ghost" onClick={() => setOpenBuildTemplatePopup(true)}>
							<LayoutTemplate className="w-4 h-4 mr-1" />
							<span>Create Template</span>
						</Button>
					</div>
				</div>
			)}

			{hasDetails ? (
				<Details
					details={siteDetails}
					handleSubmit={handleEditDeleteDetail}
					onAdd={addDetail}
				/>
			) : (
				<LocationDetailsEmptyState
					onUploadTemplate={handleAddTemplate}
					onCreateDetail={handleAddDetail}
				/>
			)}

			{openTemplatePopup ? (
				<Popup
					title="Site Detail Templates"
					openPopup={openTemplatePopup}
					setOpenPopup={setOpenTemplatePopup}
					icon={<ClipboardList className="w-5 h-5" />}
					description="Edit detail information"
				>
					<SiteDetailTemplatesSelectCN
						detailTemplates={siteDetailTemplates}
						handleTemplateLoad={handleTemplateLoad}
					/>
				</Popup>
			) : null}

			{openBuildTemplatePopup && (
				<Popup
					title="Details Template"
					openPopup={openBuildTemplatePopup}
					setOpenPopup={setOpenBuildTemplatePopup}
					popupWidth="lg"
					icon={<LayoutTemplate className="w-5 h-5" />}
					description="Create a new details template"
				>
					<BuildSiteDetailTemplateModal
						details={siteDetails}
						openPopup={openBuildTemplatePopup}
						setOpenPopup={setOpenBuildTemplatePopup}
						onBuildTemplate={async templateData => {
							try {
								await dispatch(buildTemplateFromSiteDetails(templateData))
								setOpenBuildTemplatePopup(false)
								dispatch(initializeTemplates())
								notify({
									title: 'Template Created!',
									text: `The template "${templateData.templateName}" has been created successfully.`,
									icon: 'success',
								})
							} catch (error) {
								notify({
									title: 'Error',
									text: error.message || 'Failed to create template',
									icon: 'error',
								})
							}
						}}
					/>
				</Popup>
			)}
		{duplicateMatches.length > 0 && (
				<DeletedDuplicateDialog
					open={duplicateDialogOpen}
					onClose={() => {
						setDuplicateDialogOpen(false)
						setDuplicateMatches([])
						setPendingDetailPayload(null)
						setPendingResetForm(null)
						setPendingTemplateId(null)
					}}
					itemType="site detail"
					matches={duplicateMatches}
					onRestore={handleDetailDuplicateRestore}
					onCreateNew={handleDetailDuplicateCreateNew}
					isLoading={isDuplicateLoading}
				/>
			)}
		</div>
	)
}

export default LocationDetailsScreen
